/**
 * shadcn/ui v4 preset data — full 6-axis enumeration.
 *
 * Source of truth: `_upstream/shadcn-ui-ui/apps/v4/registry/`
 *   - base-colors.ts  → 7 base colors (subset of THEMES)
 *   - themes.ts       → 7 base + 17 chart/overlay theme colors
 *   - config.ts       → RADII, PRESETS, getThemesForBaseColor
 *   - fonts.ts + lib/font-definitions.ts → font preset list
 *   - app/(app)/create/lib/merge-theme.ts → canonical merge order
 *
 * Mechanism (mirrors shadcn /create):
 *  - Style is applied as a body/html class: `style-{name}`. The CSS rules
 *    live in `packages/cogo-styles/src/style-{name}.css` (only style-luma.css
 *    is currently shipped — see Phase 329 SUMMARY for the gap).
 *  - Base Color, Theme overlay, Chart palette, Radius, and Font are applied
 *    by injecting a single `<style id="cogo-theme-vars">:root { ... }</style>`
 *    block into <head>. shadcn upstream uses id `design-system-theme-vars`.
 *
 * Merge order (D-06, identical to upstream `merge-theme.ts`):
 *   light = { ...baseColor.light, ...theme?.light }   // theme overlay wins
 *   then chart-1..5 from chartTheme override (per-key, see buildRegistryTheme)
 *   then '--radius' override
 *
 * oklch values are copied verbatim from upstream — no parsing, no hsl()
 * conversion. See feedback_oklch_passthrough.md.
 */

import { FONT_STACKS } from './font-stacks'

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export type ThemeVars = Record<string, string>

export interface BaseColorPreset {
  name: string
  light: ThemeVars
  dark: ThemeVars
}

export interface ThemePreset {
  name: string
  light: ThemeVars
  dark: ThemeVars
}

export interface StylePreset {
  name: string
  className: string
  cssPath: string
}

export interface FontPreset {
  name: string
  value: string
}

export interface RadiusPreset {
  name: string
  label: string
  value: string
}

// ─────────────────────────────────────────────────────────────────────────────
// BASE COLORS — 7 entries from upstream base-colors.ts
// (subset of themes.ts where name ∈ {neutral,stone,zinc,mauve,olive,mist,taupe})
// Each contains the full ThemeVars set (light + dark).
// Values copied verbatim from _upstream/.../registry/themes.ts.
// ─────────────────────────────────────────────────────────────────────────────

const NEUTRAL_LIGHT: ThemeVars = {
  background: 'oklch(1 0 0)',
  foreground: 'oklch(0.145 0 0)',
  card: 'oklch(1 0 0)',
  'card-foreground': 'oklch(0.145 0 0)',
  popover: 'oklch(1 0 0)',
  'popover-foreground': 'oklch(0.145 0 0)',
  primary: 'oklch(0.205 0 0)',
  'primary-foreground': 'oklch(0.985 0 0)',
  secondary: 'oklch(0.97 0 0)',
  'secondary-foreground': 'oklch(0.205 0 0)',
  muted: 'oklch(0.97 0 0)',
  'muted-foreground': 'oklch(0.556 0 0)',
  accent: 'oklch(0.97 0 0)',
  'accent-foreground': 'oklch(0.205 0 0)',
  destructive: 'oklch(0.577 0.245 27.325)',
  border: 'oklch(0.922 0 0)',
  input: 'oklch(0.922 0 0)',
  ring: 'oklch(0.708 0 0)',
  'chart-1': 'oklch(0.87 0 0)',
  'chart-2': 'oklch(0.556 0 0)',
  'chart-3': 'oklch(0.439 0 0)',
  'chart-4': 'oklch(0.371 0 0)',
  'chart-5': 'oklch(0.269 0 0)',
  radius: '0.625rem',
  sidebar: 'oklch(0.985 0 0)',
  'sidebar-foreground': 'oklch(0.145 0 0)',
  'sidebar-primary': 'oklch(0.205 0 0)',
  'sidebar-primary-foreground': 'oklch(0.985 0 0)',
  'sidebar-accent': 'oklch(0.97 0 0)',
  'sidebar-accent-foreground': 'oklch(0.205 0 0)',
  'sidebar-border': 'oklch(0.922 0 0)',
  'sidebar-ring': 'oklch(0.708 0 0)',
}

const NEUTRAL_DARK: ThemeVars = {
  background: 'oklch(0.145 0 0)',
  foreground: 'oklch(0.985 0 0)',
  card: 'oklch(0.205 0 0)',
  'card-foreground': 'oklch(0.985 0 0)',
  popover: 'oklch(0.205 0 0)',
  'popover-foreground': 'oklch(0.985 0 0)',
  primary: 'oklch(0.922 0 0)',
  'primary-foreground': 'oklch(0.205 0 0)',
  secondary: 'oklch(0.269 0 0)',
  'secondary-foreground': 'oklch(0.985 0 0)',
  muted: 'oklch(0.269 0 0)',
  'muted-foreground': 'oklch(0.708 0 0)',
  accent: 'oklch(0.269 0 0)',
  'accent-foreground': 'oklch(0.985 0 0)',
  destructive: 'oklch(0.704 0.191 22.216)',
  border: 'oklch(1 0 0 / 10%)',
  input: 'oklch(1 0 0 / 15%)',
  ring: 'oklch(0.556 0 0)',
  'chart-1': 'oklch(0.87 0 0)',
  'chart-2': 'oklch(0.556 0 0)',
  'chart-3': 'oklch(0.439 0 0)',
  'chart-4': 'oklch(0.371 0 0)',
  'chart-5': 'oklch(0.269 0 0)',
  sidebar: 'oklch(0.205 0 0)',
  'sidebar-foreground': 'oklch(0.985 0 0)',
  'sidebar-primary': 'oklch(0.488 0.243 264.376)',
  'sidebar-primary-foreground': 'oklch(0.985 0 0)',
  'sidebar-accent': 'oklch(0.269 0 0)',
  'sidebar-accent-foreground': 'oklch(0.985 0 0)',
  'sidebar-border': 'oklch(1 0 0 / 10%)',
  'sidebar-ring': 'oklch(0.556 0 0)',
}

const STONE_LIGHT: ThemeVars = {
  background: 'oklch(1 0 0)',
  foreground: 'oklch(0.147 0.004 49.25)',
  card: 'oklch(1 0 0)',
  'card-foreground': 'oklch(0.147 0.004 49.25)',
  popover: 'oklch(1 0 0)',
  'popover-foreground': 'oklch(0.147 0.004 49.25)',
  primary: 'oklch(0.216 0.006 56.043)',
  'primary-foreground': 'oklch(0.985 0.001 106.423)',
  secondary: 'oklch(0.97 0.001 106.424)',
  'secondary-foreground': 'oklch(0.216 0.006 56.043)',
  muted: 'oklch(0.97 0.001 106.424)',
  'muted-foreground': 'oklch(0.553 0.013 58.071)',
  accent: 'oklch(0.97 0.001 106.424)',
  'accent-foreground': 'oklch(0.216 0.006 56.043)',
  destructive: 'oklch(0.577 0.245 27.325)',
  border: 'oklch(0.923 0.003 48.717)',
  input: 'oklch(0.923 0.003 48.717)',
  ring: 'oklch(0.709 0.01 56.259)',
  'chart-1': 'oklch(0.869 0.005 56.366)',
  'chart-2': 'oklch(0.553 0.013 58.071)',
  'chart-3': 'oklch(0.444 0.011 73.639)',
  'chart-4': 'oklch(0.374 0.01 67.558)',
  'chart-5': 'oklch(0.268 0.007 34.298)',
  radius: '0.625rem',
  sidebar: 'oklch(0.985 0.001 106.423)',
  'sidebar-foreground': 'oklch(0.147 0.004 49.25)',
  'sidebar-primary': 'oklch(0.216 0.006 56.043)',
  'sidebar-primary-foreground': 'oklch(0.985 0.001 106.423)',
  'sidebar-accent': 'oklch(0.97 0.001 106.424)',
  'sidebar-accent-foreground': 'oklch(0.216 0.006 56.043)',
  'sidebar-border': 'oklch(0.923 0.003 48.717)',
  'sidebar-ring': 'oklch(0.709 0.01 56.259)',
}

const STONE_DARK: ThemeVars = {
  background: 'oklch(0.147 0.004 49.25)',
  foreground: 'oklch(0.985 0.001 106.423)',
  card: 'oklch(0.216 0.006 56.043)',
  'card-foreground': 'oklch(0.985 0.001 106.423)',
  popover: 'oklch(0.216 0.006 56.043)',
  'popover-foreground': 'oklch(0.985 0.001 106.423)',
  primary: 'oklch(0.923 0.003 48.717)',
  'primary-foreground': 'oklch(0.216 0.006 56.043)',
  secondary: 'oklch(0.268 0.007 34.298)',
  'secondary-foreground': 'oklch(0.985 0.001 106.423)',
  muted: 'oklch(0.268 0.007 34.298)',
  'muted-foreground': 'oklch(0.709 0.01 56.259)',
  accent: 'oklch(0.268 0.007 34.298)',
  'accent-foreground': 'oklch(0.985 0.001 106.423)',
  destructive: 'oklch(0.704 0.191 22.216)',
  border: 'oklch(1 0 0 / 10%)',
  input: 'oklch(1 0 0 / 15%)',
  ring: 'oklch(0.553 0.013 58.071)',
  'chart-1': 'oklch(0.869 0.005 56.366)',
  'chart-2': 'oklch(0.553 0.013 58.071)',
  'chart-3': 'oklch(0.444 0.011 73.639)',
  'chart-4': 'oklch(0.374 0.01 67.558)',
  'chart-5': 'oklch(0.268 0.007 34.298)',
  sidebar: 'oklch(0.216 0.006 56.043)',
  'sidebar-foreground': 'oklch(0.985 0.001 106.423)',
  'sidebar-primary': 'oklch(0.488 0.243 264.376)',
  'sidebar-primary-foreground': 'oklch(0.985 0.001 106.423)',
  'sidebar-accent': 'oklch(0.268 0.007 34.298)',
  'sidebar-accent-foreground': 'oklch(0.985 0.001 106.423)',
  'sidebar-border': 'oklch(1 0 0 / 10%)',
  'sidebar-ring': 'oklch(0.553 0.013 58.071)',
}

const ZINC_LIGHT: ThemeVars = {
  background: 'oklch(1 0 0)',
  foreground: 'oklch(0.141 0.005 285.823)',
  card: 'oklch(1 0 0)',
  'card-foreground': 'oklch(0.141 0.005 285.823)',
  popover: 'oklch(1 0 0)',
  'popover-foreground': 'oklch(0.141 0.005 285.823)',
  primary: 'oklch(0.21 0.006 285.885)',
  'primary-foreground': 'oklch(0.985 0 0)',
  secondary: 'oklch(0.967 0.001 286.375)',
  'secondary-foreground': 'oklch(0.21 0.006 285.885)',
  muted: 'oklch(0.967 0.001 286.375)',
  'muted-foreground': 'oklch(0.552 0.016 285.938)',
  accent: 'oklch(0.967 0.001 286.375)',
  'accent-foreground': 'oklch(0.21 0.006 285.885)',
  destructive: 'oklch(0.577 0.245 27.325)',
  border: 'oklch(0.92 0.004 286.32)',
  input: 'oklch(0.92 0.004 286.32)',
  ring: 'oklch(0.705 0.015 286.067)',
  'chart-1': 'oklch(0.871 0.006 286.286)',
  'chart-2': 'oklch(0.552 0.016 285.938)',
  'chart-3': 'oklch(0.442 0.017 285.786)',
  'chart-4': 'oklch(0.37 0.013 285.805)',
  'chart-5': 'oklch(0.274 0.006 286.033)',
  radius: '0.625rem',
  sidebar: 'oklch(0.985 0 0)',
  'sidebar-foreground': 'oklch(0.141 0.005 285.823)',
  'sidebar-primary': 'oklch(0.21 0.006 285.885)',
  'sidebar-primary-foreground': 'oklch(0.985 0 0)',
  'sidebar-accent': 'oklch(0.967 0.001 286.375)',
  'sidebar-accent-foreground': 'oklch(0.21 0.006 285.885)',
  'sidebar-border': 'oklch(0.92 0.004 286.32)',
  'sidebar-ring': 'oklch(0.705 0.015 286.067)',
}

const ZINC_DARK: ThemeVars = {
  background: 'oklch(0.141 0.005 285.823)',
  foreground: 'oklch(0.985 0 0)',
  card: 'oklch(0.21 0.006 285.885)',
  'card-foreground': 'oklch(0.985 0 0)',
  popover: 'oklch(0.21 0.006 285.885)',
  'popover-foreground': 'oklch(0.985 0 0)',
  primary: 'oklch(0.92 0.004 286.32)',
  'primary-foreground': 'oklch(0.21 0.006 285.885)',
  secondary: 'oklch(0.274 0.006 286.033)',
  'secondary-foreground': 'oklch(0.985 0 0)',
  muted: 'oklch(0.274 0.006 286.033)',
  'muted-foreground': 'oklch(0.705 0.015 286.067)',
  accent: 'oklch(0.274 0.006 286.033)',
  'accent-foreground': 'oklch(0.985 0 0)',
  destructive: 'oklch(0.704 0.191 22.216)',
  border: 'oklch(1 0 0 / 10%)',
  input: 'oklch(1 0 0 / 15%)',
  ring: 'oklch(0.552 0.016 285.938)',
  'chart-1': 'oklch(0.871 0.006 286.286)',
  'chart-2': 'oklch(0.552 0.016 285.938)',
  'chart-3': 'oklch(0.442 0.017 285.786)',
  'chart-4': 'oklch(0.37 0.013 285.805)',
  'chart-5': 'oklch(0.274 0.006 286.033)',
  sidebar: 'oklch(0.21 0.006 285.885)',
  'sidebar-foreground': 'oklch(0.985 0 0)',
  'sidebar-primary': 'oklch(0.488 0.243 264.376)',
  'sidebar-primary-foreground': 'oklch(0.985 0 0)',
  'sidebar-accent': 'oklch(0.274 0.006 286.033)',
  'sidebar-accent-foreground': 'oklch(0.985 0 0)',
  'sidebar-border': 'oklch(1 0 0 / 10%)',
  'sidebar-ring': 'oklch(0.552 0.016 285.938)',
}

const MAUVE_LIGHT: ThemeVars = {
  background: 'oklch(1 0 0)',
  foreground: 'oklch(0.145 0.008 326)',
  card: 'oklch(1 0 0)',
  'card-foreground': 'oklch(0.145 0.008 326)',
  popover: 'oklch(1 0 0)',
  'popover-foreground': 'oklch(0.145 0.008 326)',
  primary: 'oklch(0.212 0.019 322.12)',
  'primary-foreground': 'oklch(0.985 0 0)',
  secondary: 'oklch(0.96 0.003 325.6)',
  'secondary-foreground': 'oklch(0.212 0.019 322.12)',
  muted: 'oklch(0.96 0.003 325.6)',
  'muted-foreground': 'oklch(0.542 0.034 322.5)',
  accent: 'oklch(0.96 0.003 325.6)',
  'accent-foreground': 'oklch(0.212 0.019 322.12)',
  destructive: 'oklch(0.577 0.245 27.325)',
  border: 'oklch(0.922 0.005 325.62)',
  input: 'oklch(0.922 0.005 325.62)',
  ring: 'oklch(0.711 0.019 323.02)',
  'chart-1': 'oklch(0.865 0.012 325.68)',
  'chart-2': 'oklch(0.542 0.034 322.5)',
  'chart-3': 'oklch(0.435 0.029 321.78)',
  'chart-4': 'oklch(0.364 0.029 323.89)',
  'chart-5': 'oklch(0.263 0.024 320.12)',
  radius: '0.625rem',
  sidebar: 'oklch(0.985 0 0)',
  'sidebar-foreground': 'oklch(0.145 0.008 326)',
  'sidebar-primary': 'oklch(0.212 0.019 322.12)',
  'sidebar-primary-foreground': 'oklch(0.985 0 0)',
  'sidebar-accent': 'oklch(0.96 0.003 325.6)',
  'sidebar-accent-foreground': 'oklch(0.212 0.019 322.12)',
  'sidebar-border': 'oklch(0.922 0.005 325.62)',
  'sidebar-ring': 'oklch(0.711 0.019 323.02)',
}

const MAUVE_DARK: ThemeVars = {
  background: 'oklch(0.145 0.008 326)',
  foreground: 'oklch(0.985 0 0)',
  card: 'oklch(0.212 0.019 322.12)',
  'card-foreground': 'oklch(0.985 0 0)',
  popover: 'oklch(0.212 0.019 322.12)',
  'popover-foreground': 'oklch(0.985 0 0)',
  primary: 'oklch(0.922 0.005 325.62)',
  'primary-foreground': 'oklch(0.212 0.019 322.12)',
  secondary: 'oklch(0.263 0.024 320.12)',
  'secondary-foreground': 'oklch(0.985 0 0)',
  muted: 'oklch(0.263 0.024 320.12)',
  'muted-foreground': 'oklch(0.711 0.019 323.02)',
  accent: 'oklch(0.263 0.024 320.12)',
  'accent-foreground': 'oklch(0.985 0 0)',
  destructive: 'oklch(0.704 0.191 22.216)',
  border: 'oklch(1 0 0 / 10%)',
  input: 'oklch(1 0 0 / 15%)',
  ring: 'oklch(0.542 0.034 322.5)',
  'chart-1': 'oklch(0.865 0.012 325.68)',
  'chart-2': 'oklch(0.542 0.034 322.5)',
  'chart-3': 'oklch(0.435 0.029 321.78)',
  'chart-4': 'oklch(0.364 0.029 323.89)',
  'chart-5': 'oklch(0.263 0.024 320.12)',
  sidebar: 'oklch(0.212 0.019 322.12)',
  'sidebar-foreground': 'oklch(0.985 0 0)',
  'sidebar-primary': 'oklch(0.488 0.243 264.376)',
  'sidebar-primary-foreground': 'oklch(0.985 0 0)',
  'sidebar-accent': 'oklch(0.263 0.024 320.12)',
  'sidebar-accent-foreground': 'oklch(0.985 0 0)',
  'sidebar-border': 'oklch(1 0 0 / 10%)',
  'sidebar-ring': 'oklch(0.542 0.034 322.5)',
}

const OLIVE_LIGHT: ThemeVars = {
  background: 'oklch(1 0 0)',
  foreground: 'oklch(0.153 0.006 107.1)',
  card: 'oklch(1 0 0)',
  'card-foreground': 'oklch(0.153 0.006 107.1)',
  popover: 'oklch(1 0 0)',
  'popover-foreground': 'oklch(0.153 0.006 107.1)',
  primary: 'oklch(0.228 0.013 107.4)',
  'primary-foreground': 'oklch(0.988 0.003 106.5)',
  secondary: 'oklch(0.966 0.005 106.5)',
  'secondary-foreground': 'oklch(0.228 0.013 107.4)',
  muted: 'oklch(0.966 0.005 106.5)',
  'muted-foreground': 'oklch(0.58 0.031 107.3)',
  accent: 'oklch(0.966 0.005 106.5)',
  'accent-foreground': 'oklch(0.228 0.013 107.4)',
  destructive: 'oklch(0.577 0.245 27.325)',
  border: 'oklch(0.93 0.007 106.5)',
  input: 'oklch(0.93 0.007 106.5)',
  ring: 'oklch(0.737 0.021 106.9)',
  'chart-1': 'oklch(0.88 0.011 106.6)',
  'chart-2': 'oklch(0.58 0.031 107.3)',
  'chart-3': 'oklch(0.466 0.025 107.3)',
  'chart-4': 'oklch(0.394 0.023 107.4)',
  'chart-5': 'oklch(0.286 0.016 107.4)',
  radius: '0.625rem',
  sidebar: 'oklch(0.988 0.003 106.5)',
  'sidebar-foreground': 'oklch(0.153 0.006 107.1)',
  'sidebar-primary': 'oklch(0.228 0.013 107.4)',
  'sidebar-primary-foreground': 'oklch(0.988 0.003 106.5)',
  'sidebar-accent': 'oklch(0.966 0.005 106.5)',
  'sidebar-accent-foreground': 'oklch(0.228 0.013 107.4)',
  'sidebar-border': 'oklch(0.93 0.007 106.5)',
  'sidebar-ring': 'oklch(0.737 0.021 106.9)',
}

const OLIVE_DARK: ThemeVars = {
  background: 'oklch(0.153 0.006 107.1)',
  foreground: 'oklch(0.988 0.003 106.5)',
  card: 'oklch(0.228 0.013 107.4)',
  'card-foreground': 'oklch(0.988 0.003 106.5)',
  popover: 'oklch(0.228 0.013 107.4)',
  'popover-foreground': 'oklch(0.988 0.003 106.5)',
  primary: 'oklch(0.93 0.007 106.5)',
  'primary-foreground': 'oklch(0.228 0.013 107.4)',
  secondary: 'oklch(0.286 0.016 107.4)',
  'secondary-foreground': 'oklch(0.988 0.003 106.5)',
  muted: 'oklch(0.286 0.016 107.4)',
  'muted-foreground': 'oklch(0.737 0.021 106.9)',
  accent: 'oklch(0.286 0.016 107.4)',
  'accent-foreground': 'oklch(0.988 0.003 106.5)',
  destructive: 'oklch(0.704 0.191 22.216)',
  border: 'oklch(1 0 0 / 10%)',
  input: 'oklch(1 0 0 / 15%)',
  ring: 'oklch(0.58 0.031 107.3)',
  'chart-1': 'oklch(0.88 0.011 106.6)',
  'chart-2': 'oklch(0.58 0.031 107.3)',
  'chart-3': 'oklch(0.466 0.025 107.3)',
  'chart-4': 'oklch(0.394 0.023 107.4)',
  'chart-5': 'oklch(0.286 0.016 107.4)',
  sidebar: 'oklch(0.228 0.013 107.4)',
  'sidebar-foreground': 'oklch(0.988 0.003 106.5)',
  'sidebar-primary': 'oklch(0.488 0.243 264.376)',
  'sidebar-primary-foreground': 'oklch(0.988 0.003 106.5)',
  'sidebar-accent': 'oklch(0.286 0.016 107.4)',
  'sidebar-accent-foreground': 'oklch(0.988 0.003 106.5)',
  'sidebar-border': 'oklch(1 0 0 / 10%)',
  'sidebar-ring': 'oklch(0.58 0.031 107.3)',
}

const MIST_LIGHT: ThemeVars = {
  background: 'oklch(1 0 0)',
  foreground: 'oklch(0.148 0.004 228.8)',
  card: 'oklch(1 0 0)',
  'card-foreground': 'oklch(0.148 0.004 228.8)',
  popover: 'oklch(1 0 0)',
  'popover-foreground': 'oklch(0.148 0.004 228.8)',
  primary: 'oklch(0.218 0.008 223.9)',
  'primary-foreground': 'oklch(0.987 0.002 197.1)',
  secondary: 'oklch(0.963 0.002 197.1)',
  'secondary-foreground': 'oklch(0.218 0.008 223.9)',
  muted: 'oklch(0.963 0.002 197.1)',
  'muted-foreground': 'oklch(0.56 0.021 213.5)',
  accent: 'oklch(0.963 0.002 197.1)',
  'accent-foreground': 'oklch(0.218 0.008 223.9)',
  destructive: 'oklch(0.577 0.245 27.325)',
  border: 'oklch(0.925 0.005 214.3)',
  input: 'oklch(0.925 0.005 214.3)',
  ring: 'oklch(0.723 0.014 214.4)',
  'chart-1': 'oklch(0.872 0.007 219.6)',
  'chart-2': 'oklch(0.56 0.021 213.5)',
  'chart-3': 'oklch(0.45 0.017 213.2)',
  'chart-4': 'oklch(0.378 0.015 216)',
  'chart-5': 'oklch(0.275 0.011 216.9)',
  radius: '0.625rem',
  sidebar: 'oklch(0.987 0.002 197.1)',
  'sidebar-foreground': 'oklch(0.148 0.004 228.8)',
  'sidebar-primary': 'oklch(0.218 0.008 223.9)',
  'sidebar-primary-foreground': 'oklch(0.987 0.002 197.1)',
  'sidebar-accent': 'oklch(0.963 0.002 197.1)',
  'sidebar-accent-foreground': 'oklch(0.218 0.008 223.9)',
  'sidebar-border': 'oklch(0.925 0.005 214.3)',
  'sidebar-ring': 'oklch(0.723 0.014 214.4)',
}

const MIST_DARK: ThemeVars = {
  background: 'oklch(0.148 0.004 228.8)',
  foreground: 'oklch(0.987 0.002 197.1)',
  card: 'oklch(0.218 0.008 223.9)',
  'card-foreground': 'oklch(0.987 0.002 197.1)',
  popover: 'oklch(0.218 0.008 223.9)',
  'popover-foreground': 'oklch(0.987 0.002 197.1)',
  primary: 'oklch(0.925 0.005 214.3)',
  'primary-foreground': 'oklch(0.218 0.008 223.9)',
  secondary: 'oklch(0.275 0.011 216.9)',
  'secondary-foreground': 'oklch(0.987 0.002 197.1)',
  muted: 'oklch(0.275 0.011 216.9)',
  'muted-foreground': 'oklch(0.723 0.014 214.4)',
  accent: 'oklch(0.275 0.011 216.9)',
  'accent-foreground': 'oklch(0.987 0.002 197.1)',
  destructive: 'oklch(0.704 0.191 22.216)',
  border: 'oklch(1 0 0 / 10%)',
  input: 'oklch(1 0 0 / 15%)',
  ring: 'oklch(0.56 0.021 213.5)',
  'chart-1': 'oklch(0.872 0.007 219.6)',
  'chart-2': 'oklch(0.56 0.021 213.5)',
  'chart-3': 'oklch(0.45 0.017 213.2)',
  'chart-4': 'oklch(0.378 0.015 216)',
  'chart-5': 'oklch(0.275 0.011 216.9)',
  sidebar: 'oklch(0.218 0.008 223.9)',
  'sidebar-foreground': 'oklch(0.987 0.002 197.1)',
  'sidebar-primary': 'oklch(0.488 0.243 264.376)',
  'sidebar-primary-foreground': 'oklch(0.987 0.002 197.1)',
  'sidebar-accent': 'oklch(0.275 0.011 216.9)',
  'sidebar-accent-foreground': 'oklch(0.987 0.002 197.1)',
  'sidebar-border': 'oklch(1 0 0 / 10%)',
  'sidebar-ring': 'oklch(0.56 0.021 213.5)',
}

const TAUPE_LIGHT: ThemeVars = {
  background: 'oklch(1 0 0)',
  foreground: 'oklch(0.147 0.004 49.3)',
  card: 'oklch(1 0 0)',
  'card-foreground': 'oklch(0.147 0.004 49.3)',
  popover: 'oklch(1 0 0)',
  'popover-foreground': 'oklch(0.147 0.004 49.3)',
  primary: 'oklch(0.214 0.009 43.1)',
  'primary-foreground': 'oklch(0.986 0.002 67.8)',
  secondary: 'oklch(0.96 0.002 17.2)',
  'secondary-foreground': 'oklch(0.214 0.009 43.1)',
  muted: 'oklch(0.96 0.002 17.2)',
  'muted-foreground': 'oklch(0.547 0.021 43.1)',
  accent: 'oklch(0.96 0.002 17.2)',
  'accent-foreground': 'oklch(0.214 0.009 43.1)',
  destructive: 'oklch(0.577 0.245 27.325)',
  border: 'oklch(0.922 0.005 34.3)',
  input: 'oklch(0.922 0.005 34.3)',
  ring: 'oklch(0.714 0.014 41.2)',
  'chart-1': 'oklch(0.868 0.007 39.5)',
  'chart-2': 'oklch(0.547 0.021 43.1)',
  'chart-3': 'oklch(0.438 0.017 39.3)',
  'chart-4': 'oklch(0.367 0.016 35.7)',
  'chart-5': 'oklch(0.268 0.011 36.5)',
  radius: '0.625rem',
  sidebar: 'oklch(0.986 0.002 67.8)',
  'sidebar-foreground': 'oklch(0.147 0.004 49.3)',
  'sidebar-primary': 'oklch(0.214 0.009 43.1)',
  'sidebar-primary-foreground': 'oklch(0.986 0.002 67.8)',
  'sidebar-accent': 'oklch(0.96 0.002 17.2)',
  'sidebar-accent-foreground': 'oklch(0.214 0.009 43.1)',
  'sidebar-border': 'oklch(0.922 0.005 34.3)',
  'sidebar-ring': 'oklch(0.714 0.014 41.2)',
}

const TAUPE_DARK: ThemeVars = {
  background: 'oklch(0.147 0.004 49.3)',
  foreground: 'oklch(0.986 0.002 67.8)',
  card: 'oklch(0.214 0.009 43.1)',
  'card-foreground': 'oklch(0.986 0.002 67.8)',
  popover: 'oklch(0.214 0.009 43.1)',
  'popover-foreground': 'oklch(0.986 0.002 67.8)',
  primary: 'oklch(0.922 0.005 34.3)',
  'primary-foreground': 'oklch(0.214 0.009 43.1)',
  secondary: 'oklch(0.268 0.011 36.5)',
  'secondary-foreground': 'oklch(0.986 0.002 67.8)',
  muted: 'oklch(0.268 0.011 36.5)',
  'muted-foreground': 'oklch(0.714 0.014 41.2)',
  accent: 'oklch(0.268 0.011 36.5)',
  'accent-foreground': 'oklch(0.986 0.002 67.8)',
  destructive: 'oklch(0.704 0.191 22.216)',
  border: 'oklch(1 0 0 / 10%)',
  input: 'oklch(1 0 0 / 15%)',
  ring: 'oklch(0.547 0.021 43.1)',
  'chart-1': 'oklch(0.868 0.007 39.5)',
  'chart-2': 'oklch(0.547 0.021 43.1)',
  'chart-3': 'oklch(0.438 0.017 39.3)',
  'chart-4': 'oklch(0.367 0.016 35.7)',
  'chart-5': 'oklch(0.268 0.011 36.5)',
  sidebar: 'oklch(0.214 0.009 43.1)',
  'sidebar-foreground': 'oklch(0.986 0.002 67.8)',
  'sidebar-primary': 'oklch(0.488 0.243 264.376)',
  'sidebar-primary-foreground': 'oklch(0.986 0.002 67.8)',
  'sidebar-accent': 'oklch(0.268 0.011 36.5)',
  'sidebar-accent-foreground': 'oklch(0.986 0.002 67.8)',
  'sidebar-border': 'oklch(1 0 0 / 10%)',
  'sidebar-ring': 'oklch(0.547 0.021 43.1)',
}

/**
 * BASE_COLORS — 7 entries (subset of THEMES marked as base colors upstream).
 * Keeps array shape for backward compatibility with BlockGridTestPage.
 */
export const BASE_COLORS: BaseColorPreset[] = [
  { name: 'neutral', light: NEUTRAL_LIGHT, dark: NEUTRAL_DARK },
  { name: 'stone', light: STONE_LIGHT, dark: STONE_DARK },
  { name: 'zinc', light: ZINC_LIGHT, dark: ZINC_DARK },
  { name: 'mauve', light: MAUVE_LIGHT, dark: MAUVE_DARK },
  { name: 'olive', light: OLIVE_LIGHT, dark: OLIVE_DARK },
  { name: 'mist', light: MIST_LIGHT, dark: MIST_DARK },
  { name: 'taupe', light: TAUPE_LIGHT, dark: TAUPE_DARK },
]

/** Indexed lookup by base color name. */
export const BASE_COLORS_BY_NAME: Record<string, BaseColorPreset> = Object.fromEntries(
  BASE_COLORS.map((bc) => [bc.name, bc]),
)

// ─────────────────────────────────────────────────────────────────────────────
// THEMES (chart/overlay themes) — 17 entries from upstream themes.ts
// These are partial overlays; only contain primary, secondary, chart-1..5,
// and sidebar-primary keys. They are spread AFTER baseColor in the merge.
// Per upstream `getThemesForBaseColor`: any theme NOT in BASE_COLOR names is
// available for any base color.
// ─────────────────────────────────────────────────────────────────────────────

const THEMES_DATA: ThemePreset[] = [
  // Phase 396 fix: add a "default" entry first so Base Color's neutral tints
  // (foreground, card, muted, border) are visible. Previously the first Theme
  // was 'amber' whose bright primary/secondary overrides completely masked
  // Base Color's subtle grayscale hue shifts — users saw no visible effect
  // when switching Base Color because all accents came from the Theme layer.
  // The 'default' preset has empty light/dark maps — lookupPreset's merge
  // (`{ ...basePreset.light, ...(themePreset?.light ?? {}) }`) then leaves
  // the base color's own primary/secondary/chart values intact.
  {
    name: 'default',
    light: {},
    dark: {},
  },
  {
    name: 'amber',
    light: {
      primary: 'oklch(0.555 0.163 48.998)',
      'primary-foreground': 'oklch(0.987 0.022 95.277)',
      secondary: 'oklch(0.967 0.001 286.375)',
      'secondary-foreground': 'oklch(0.21 0.006 285.885)',
      'chart-1': 'oklch(0.879 0.169 91.605)',
      'chart-2': 'oklch(0.769 0.188 70.08)',
      'chart-3': 'oklch(0.666 0.179 58.318)',
      'chart-4': 'oklch(0.555 0.163 48.998)',
      'chart-5': 'oklch(0.473 0.137 46.201)',
      'sidebar-primary': 'oklch(0.666 0.179 58.318)',
      'sidebar-primary-foreground': 'oklch(0.987 0.022 95.277)',
    },
    dark: {
      primary: 'oklch(0.473 0.137 46.201)',
      'primary-foreground': 'oklch(0.987 0.022 95.277)',
      secondary: 'oklch(0.274 0.006 286.033)',
      'secondary-foreground': 'oklch(0.985 0 0)',
      'chart-1': 'oklch(0.879 0.169 91.605)',
      'chart-2': 'oklch(0.769 0.188 70.08)',
      'chart-3': 'oklch(0.666 0.179 58.318)',
      'chart-4': 'oklch(0.555 0.163 48.998)',
      'chart-5': 'oklch(0.473 0.137 46.201)',
      'sidebar-primary': 'oklch(0.769 0.188 70.08)',
      'sidebar-primary-foreground': 'oklch(0.279 0.077 45.635)',
    },
  },
  {
    name: 'blue',
    light: {
      primary: 'oklch(0.488 0.243 264.376)',
      'primary-foreground': 'oklch(0.97 0.014 254.604)',
      secondary: 'oklch(0.967 0.001 286.375)',
      'secondary-foreground': 'oklch(0.21 0.006 285.885)',
      'chart-1': 'oklch(0.809 0.105 251.813)',
      'chart-2': 'oklch(0.623 0.214 259.815)',
      'chart-3': 'oklch(0.546 0.245 262.881)',
      'chart-4': 'oklch(0.488 0.243 264.376)',
      'chart-5': 'oklch(0.424 0.199 265.638)',
      'sidebar-primary': 'oklch(0.546 0.245 262.881)',
      'sidebar-primary-foreground': 'oklch(0.97 0.014 254.604)',
    },
    dark: {
      primary: 'oklch(0.424 0.199 265.638)',
      'primary-foreground': 'oklch(0.97 0.014 254.604)',
      secondary: 'oklch(0.274 0.006 286.033)',
      'secondary-foreground': 'oklch(0.985 0 0)',
      'chart-1': 'oklch(0.809 0.105 251.813)',
      'chart-2': 'oklch(0.623 0.214 259.815)',
      'chart-3': 'oklch(0.546 0.245 262.881)',
      'chart-4': 'oklch(0.488 0.243 264.376)',
      'chart-5': 'oklch(0.424 0.199 265.638)',
      'sidebar-primary': 'oklch(0.623 0.214 259.815)',
      'sidebar-primary-foreground': 'oklch(0.97 0.014 254.604)',
    },
  },
  {
    name: 'cyan',
    light: {
      primary: 'oklch(0.52 0.105 223.128)',
      'primary-foreground': 'oklch(0.984 0.019 200.873)',
      'chart-1': 'oklch(0.865 0.127 207.078)',
      'chart-2': 'oklch(0.715 0.143 215.221)',
      'chart-3': 'oklch(0.609 0.126 221.723)',
      'chart-4': 'oklch(0.52 0.105 223.128)',
      'chart-5': 'oklch(0.45 0.085 224.283)',
    },
    dark: {
      primary: 'oklch(0.45 0.085 224.283)',
      'primary-foreground': 'oklch(0.984 0.019 200.873)',
      'chart-1': 'oklch(0.865 0.127 207.078)',
      'chart-2': 'oklch(0.715 0.143 215.221)',
      'chart-3': 'oklch(0.609 0.126 221.723)',
      'chart-4': 'oklch(0.52 0.105 223.128)',
      'chart-5': 'oklch(0.45 0.085 224.283)',
    },
  },
  {
    name: 'emerald',
    light: {
      primary: 'oklch(0.508 0.118 165.612)',
      'primary-foreground': 'oklch(0.979 0.021 166.113)',
      'chart-1': 'oklch(0.845 0.143 164.978)',
      'chart-2': 'oklch(0.696 0.17 162.48)',
      'chart-3': 'oklch(0.596 0.145 163.225)',
      'chart-4': 'oklch(0.508 0.118 165.612)',
      'chart-5': 'oklch(0.432 0.095 166.913)',
    },
    dark: {
      primary: 'oklch(0.432 0.095 166.913)',
      'primary-foreground': 'oklch(0.979 0.021 166.113)',
      'chart-1': 'oklch(0.845 0.143 164.978)',
      'chart-2': 'oklch(0.696 0.17 162.48)',
      'chart-3': 'oklch(0.596 0.145 163.225)',
      'chart-4': 'oklch(0.508 0.118 165.612)',
      'chart-5': 'oklch(0.432 0.095 166.913)',
    },
  },
  {
    name: 'fuchsia',
    light: {
      primary: 'oklch(0.518 0.253 323.949)',
      'primary-foreground': 'oklch(0.977 0.017 320.058)',
      'chart-1': 'oklch(0.833 0.145 321.434)',
      'chart-2': 'oklch(0.667 0.295 322.15)',
      'chart-3': 'oklch(0.591 0.293 322.896)',
      'chart-4': 'oklch(0.518 0.253 323.949)',
      'chart-5': 'oklch(0.452 0.211 324.591)',
    },
    dark: {
      primary: 'oklch(0.452 0.211 324.591)',
      'primary-foreground': 'oklch(0.977 0.017 320.058)',
      'chart-1': 'oklch(0.833 0.145 321.434)',
      'chart-2': 'oklch(0.667 0.295 322.15)',
      'chart-3': 'oklch(0.591 0.293 322.896)',
      'chart-4': 'oklch(0.518 0.253 323.949)',
      'chart-5': 'oklch(0.452 0.211 324.591)',
    },
  },
  {
    name: 'green',
    light: {
      primary: 'oklch(0.527 0.154 150.069)',
      'primary-foreground': 'oklch(0.982 0.018 155.826)',
      'chart-1': 'oklch(0.871 0.15 154.449)',
      'chart-2': 'oklch(0.723 0.219 149.579)',
      'chart-3': 'oklch(0.627 0.194 149.214)',
      'chart-4': 'oklch(0.527 0.154 150.069)',
      'chart-5': 'oklch(0.448 0.119 151.328)',
    },
    dark: {
      primary: 'oklch(0.448 0.119 151.328)',
      'primary-foreground': 'oklch(0.982 0.018 155.826)',
      'chart-1': 'oklch(0.871 0.15 154.449)',
      'chart-2': 'oklch(0.723 0.219 149.579)',
      'chart-3': 'oklch(0.627 0.194 149.214)',
      'chart-4': 'oklch(0.527 0.154 150.069)',
      'chart-5': 'oklch(0.448 0.119 151.328)',
    },
  },
  {
    name: 'indigo',
    light: {
      primary: 'oklch(0.457 0.24 277.023)',
      'primary-foreground': 'oklch(0.962 0.018 272.314)',
      'chart-1': 'oklch(0.785 0.115 274.713)',
      'chart-2': 'oklch(0.585 0.233 277.117)',
      'chart-3': 'oklch(0.511 0.262 276.966)',
      'chart-4': 'oklch(0.457 0.24 277.023)',
      'chart-5': 'oklch(0.398 0.195 277.366)',
    },
    dark: {
      primary: 'oklch(0.398 0.195 277.366)',
      'primary-foreground': 'oklch(0.962 0.018 272.314)',
      'chart-1': 'oklch(0.785 0.115 274.713)',
      'chart-2': 'oklch(0.585 0.233 277.117)',
      'chart-3': 'oklch(0.511 0.262 276.966)',
      'chart-4': 'oklch(0.457 0.24 277.023)',
      'chart-5': 'oklch(0.398 0.195 277.366)',
    },
  },
  {
    name: 'lime',
    light: {
      primary: 'oklch(0.841 0.238 128.85)',
      'primary-foreground': 'oklch(0.405 0.101 131.063)',
      'chart-1': 'oklch(0.897 0.196 126.665)',
      'chart-2': 'oklch(0.768 0.233 130.85)',
      'chart-3': 'oklch(0.648 0.2 131.684)',
      'chart-4': 'oklch(0.532 0.157 131.589)',
      'chart-5': 'oklch(0.453 0.124 130.933)',
    },
    dark: {
      primary: 'oklch(0.768 0.233 130.85)',
      'primary-foreground': 'oklch(0.405 0.101 131.063)',
      'chart-1': 'oklch(0.897 0.196 126.665)',
      'chart-2': 'oklch(0.768 0.233 130.85)',
      'chart-3': 'oklch(0.648 0.2 131.684)',
      'chart-4': 'oklch(0.532 0.157 131.589)',
      'chart-5': 'oklch(0.453 0.124 130.933)',
    },
  },
  {
    name: 'orange',
    light: {
      primary: 'oklch(0.553 0.195 38.402)',
      'primary-foreground': 'oklch(0.98 0.016 73.684)',
      'chart-1': 'oklch(0.837 0.128 66.29)',
      'chart-2': 'oklch(0.705 0.213 47.604)',
      'chart-3': 'oklch(0.646 0.222 41.116)',
      'chart-4': 'oklch(0.553 0.195 38.402)',
      'chart-5': 'oklch(0.47 0.157 37.304)',
    },
    dark: {
      primary: 'oklch(0.47 0.157 37.304)',
      'primary-foreground': 'oklch(0.98 0.016 73.684)',
      'chart-1': 'oklch(0.837 0.128 66.29)',
      'chart-2': 'oklch(0.705 0.213 47.604)',
      'chart-3': 'oklch(0.646 0.222 41.116)',
      'chart-4': 'oklch(0.553 0.195 38.402)',
      'chart-5': 'oklch(0.47 0.157 37.304)',
    },
  },
  {
    name: 'pink',
    light: {
      primary: 'oklch(0.525 0.223 3.958)',
      'primary-foreground': 'oklch(0.971 0.014 343.198)',
      'chart-1': 'oklch(0.823 0.12 346.018)',
      'chart-2': 'oklch(0.656 0.241 354.308)',
      'chart-3': 'oklch(0.592 0.249 0.584)',
      'chart-4': 'oklch(0.525 0.223 3.958)',
      'chart-5': 'oklch(0.459 0.187 3.815)',
    },
    dark: {
      primary: 'oklch(0.459 0.187 3.815)',
      'primary-foreground': 'oklch(0.971 0.014 343.198)',
      'chart-1': 'oklch(0.823 0.12 346.018)',
      'chart-2': 'oklch(0.656 0.241 354.308)',
      'chart-3': 'oklch(0.592 0.249 0.584)',
      'chart-4': 'oklch(0.525 0.223 3.958)',
      'chart-5': 'oklch(0.459 0.187 3.815)',
    },
  },
  {
    name: 'purple',
    light: {
      primary: 'oklch(0.496 0.265 301.924)',
      'primary-foreground': 'oklch(0.977 0.014 308.299)',
      'chart-1': 'oklch(0.827 0.119 306.383)',
      'chart-2': 'oklch(0.627 0.265 303.9)',
      'chart-3': 'oklch(0.558 0.288 302.321)',
      'chart-4': 'oklch(0.496 0.265 301.924)',
      'chart-5': 'oklch(0.438 0.218 303.724)',
    },
    dark: {
      primary: 'oklch(0.438 0.218 303.724)',
      'primary-foreground': 'oklch(0.977 0.014 308.299)',
      'chart-1': 'oklch(0.827 0.119 306.383)',
      'chart-2': 'oklch(0.627 0.265 303.9)',
      'chart-3': 'oklch(0.558 0.288 302.321)',
      'chart-4': 'oklch(0.496 0.265 301.924)',
      'chart-5': 'oklch(0.438 0.218 303.724)',
    },
  },
  {
    name: 'red',
    light: {
      primary: 'oklch(0.505 0.213 27.518)',
      'primary-foreground': 'oklch(0.971 0.013 17.38)',
      'chart-1': 'oklch(0.808 0.114 19.571)',
      'chart-2': 'oklch(0.637 0.237 25.331)',
      'chart-3': 'oklch(0.577 0.245 27.325)',
      'chart-4': 'oklch(0.505 0.213 27.518)',
      'chart-5': 'oklch(0.444 0.177 26.899)',
    },
    dark: {
      primary: 'oklch(0.444 0.177 26.899)',
      'primary-foreground': 'oklch(0.971 0.013 17.38)',
      'chart-1': 'oklch(0.808 0.114 19.571)',
      'chart-2': 'oklch(0.637 0.237 25.331)',
      'chart-3': 'oklch(0.577 0.245 27.325)',
      'chart-4': 'oklch(0.505 0.213 27.518)',
      'chart-5': 'oklch(0.444 0.177 26.899)',
    },
  },
  {
    name: 'rose',
    light: {
      primary: 'oklch(0.514 0.222 16.935)',
      'primary-foreground': 'oklch(0.969 0.015 12.422)',
      'chart-1': 'oklch(0.81 0.117 11.638)',
      'chart-2': 'oklch(0.645 0.246 16.439)',
      'chart-3': 'oklch(0.586 0.253 17.585)',
      'chart-4': 'oklch(0.514 0.222 16.935)',
      'chart-5': 'oklch(0.455 0.188 13.697)',
    },
    dark: {
      primary: 'oklch(0.455 0.188 13.697)',
      'primary-foreground': 'oklch(0.969 0.015 12.422)',
      'chart-1': 'oklch(0.81 0.117 11.638)',
      'chart-2': 'oklch(0.645 0.246 16.439)',
      'chart-3': 'oklch(0.586 0.253 17.585)',
      'chart-4': 'oklch(0.514 0.222 16.935)',
      'chart-5': 'oklch(0.455 0.188 13.697)',
    },
  },
  {
    name: 'sky',
    light: {
      primary: 'oklch(0.5 0.134 242.749)',
      'primary-foreground': 'oklch(0.977 0.013 236.62)',
      'chart-1': 'oklch(0.828 0.111 230.318)',
      'chart-2': 'oklch(0.685 0.169 237.323)',
      'chart-3': 'oklch(0.588 0.158 241.966)',
      'chart-4': 'oklch(0.5 0.134 242.749)',
      'chart-5': 'oklch(0.443 0.11 240.79)',
    },
    dark: {
      primary: 'oklch(0.443 0.11 240.79)',
      'primary-foreground': 'oklch(0.977 0.013 236.62)',
      'chart-1': 'oklch(0.828 0.111 230.318)',
      'chart-2': 'oklch(0.685 0.169 237.323)',
      'chart-3': 'oklch(0.588 0.158 241.966)',
      'chart-4': 'oklch(0.5 0.134 242.749)',
      'chart-5': 'oklch(0.443 0.11 240.79)',
    },
  },
  {
    name: 'teal',
    light: {
      primary: 'oklch(0.511 0.096 186.391)',
      'primary-foreground': 'oklch(0.984 0.014 180.72)',
      'chart-1': 'oklch(0.855 0.138 181.071)',
      'chart-2': 'oklch(0.704 0.14 182.503)',
      'chart-3': 'oklch(0.6 0.118 184.704)',
      'chart-4': 'oklch(0.511 0.096 186.391)',
      'chart-5': 'oklch(0.437 0.078 188.216)',
    },
    dark: {
      primary: 'oklch(0.437 0.078 188.216)',
      'primary-foreground': 'oklch(0.984 0.014 180.72)',
      'chart-1': 'oklch(0.855 0.138 181.071)',
      'chart-2': 'oklch(0.704 0.14 182.503)',
      'chart-3': 'oklch(0.6 0.118 184.704)',
      'chart-4': 'oklch(0.511 0.096 186.391)',
      'chart-5': 'oklch(0.437 0.078 188.216)',
    },
  },
  {
    name: 'violet',
    light: {
      primary: 'oklch(0.491 0.27 292.581)',
      'primary-foreground': 'oklch(0.969 0.016 293.756)',
      'chart-1': 'oklch(0.811 0.111 293.571)',
      'chart-2': 'oklch(0.606 0.25 292.717)',
      'chart-3': 'oklch(0.541 0.281 293.009)',
      'chart-4': 'oklch(0.491 0.27 292.581)',
      'chart-5': 'oklch(0.432 0.232 292.759)',
    },
    dark: {
      primary: 'oklch(0.432 0.232 292.759)',
      'primary-foreground': 'oklch(0.969 0.016 293.756)',
      'chart-1': 'oklch(0.811 0.111 293.571)',
      'chart-2': 'oklch(0.606 0.25 292.717)',
      'chart-3': 'oklch(0.541 0.281 293.009)',
      'chart-4': 'oklch(0.491 0.27 292.581)',
      'chart-5': 'oklch(0.432 0.232 292.759)',
    },
  },
  {
    name: 'yellow',
    light: {
      primary: 'oklch(0.852 0.199 91.936)',
      'primary-foreground': 'oklch(0.421 0.095 57.708)',
      'chart-1': 'oklch(0.905 0.182 98.111)',
      'chart-2': 'oklch(0.795 0.184 86.047)',
      'chart-3': 'oklch(0.681 0.162 75.834)',
      'chart-4': 'oklch(0.554 0.135 66.442)',
      'chart-5': 'oklch(0.476 0.114 61.907)',
    },
    dark: {
      primary: 'oklch(0.795 0.184 86.047)',
      'primary-foreground': 'oklch(0.421 0.095 57.708)',
      'chart-1': 'oklch(0.905 0.182 98.111)',
      'chart-2': 'oklch(0.795 0.184 86.047)',
      'chart-3': 'oklch(0.681 0.162 75.834)',
      'chart-4': 'oklch(0.554 0.135 66.442)',
      'chart-5': 'oklch(0.476 0.114 61.907)',
    },
  },
]

/** THEMES — color overlay presets, indexed by name. */
export const THEMES: Record<string, ThemePreset> = Object.fromEntries(
  THEMES_DATA.map((t) => [t.name, t]),
)

/**
 * CHART_PALETTES — alias view of THEMES for chart-color axis. shadcn upstream
 * uses the same THEMES list for both axes (see config.ts ChartColorName), with
 * `buildRegistryTheme` extracting only --chart-1..5 from the chart selection.
 */
export const CHART_PALETTES: Record<string, ThemePreset> = THEMES

// ─────────────────────────────────────────────────────────────────────────────
// STYLES — 6 entries from upstream PRESETS (vega/nova/maia/lyra/mira/luma)
// CSS bodies live in `cogo-web/packages/cogo-styles/src/style-{name}.css`.
// NOTE (Phase 329): only style-luma.css currently ships in cogo-styles.
// The other 5 cssPath entries point to the eventual location; consumers must
// guard against missing CSS until Phase 330+ ports them.
// ─────────────────────────────────────────────────────────────────────────────

export const STYLES_LIST: StylePreset[] = [
  { name: 'vega', className: 'style-vega', cssPath: '@cogo/styles/style-vega.css' },
  { name: 'nova', className: 'style-nova', cssPath: '@cogo/styles/style-nova.css' },
  { name: 'maia', className: 'style-maia', cssPath: '@cogo/styles/style-maia.css' },
  { name: 'lyra', className: 'style-lyra', cssPath: '@cogo/styles/style-lyra.css' },
  { name: 'mira', className: 'style-mira', cssPath: '@cogo/styles/style-mira.css' },
  { name: 'luma', className: 'style-luma', cssPath: '@cogo/styles/style-luma.css' },
]

/** Indexed lookup by style name. */
export const STYLES_BY_NAME: Record<string, StylePreset> = Object.fromEntries(
  STYLES_LIST.map((s) => [s.name, s]),
)

/**
 * STYLES — readonly tuple of style names. Backward-compatible with the
 * existing BlockGridTestPage consumer (`STYLES.forEach`).
 */
export const STYLES = ['vega', 'nova', 'maia', 'lyra', 'mira', 'luma'] as const
export type Style = (typeof STYLES)[number]

// ─────────────────────────────────────────────────────────────────────────────
// FONTS — derived from upstream lib/font-definitions.ts (body fonts only,
// matching `bodyFonts.map(f => f.name.replace('font-', ''))` in config.ts).
// ─────────────────────────────────────────────────────────────────────────────

const FONT_NAMES = [
  'geist',
  'inter',
  'noto-sans',
  'nunito-sans',
  'figtree',
  'roboto',
  'raleway',
  'dm-sans',
  'public-sans',
  'outfit',
  'oxanium',
  'manrope',
  'space-grotesk',
  'montserrat',
  'ibm-plex-sans',
  'source-sans-3',
  'instrument-sans',
  'jetbrains-mono',
  'geist-mono',
  'noto-serif',
  'roboto-slab',
  'merriweather',
  'lora',
  'playfair-display',
] as const

/**
 * FONTS — Record of font preset name → concrete CSS font-family stack.
 *
 * Phase 393 (D-03): previously returned `var(--font-${name})` references that
 * nothing ever defined at runtime (a dead contract inherited from upstream
 * Next.js font loading). Now returns the real stack from FONT_STACKS so the
 * value is directly consumable by --font-sans / --font-mono / --font-heading.
 *
 * Fallback `'system-ui, sans-serif'` only fires if `FONT_NAMES` contains a name
 * that FONT_STACKS is missing — defensive, not a data path.
 */
export const FONTS: Record<string, FontPreset> = Object.fromEntries(
  FONT_NAMES.map((name) => {
    const entry = FONT_STACKS[name]
    return [name, { name, value: entry?.stack ?? 'system-ui, sans-serif' }]
  }),
)

// ─────────────────────────────────────────────────────────────────────────────
// RADII — from upstream config.ts RADII (5 entries).
// Upstream values: default(""), none("0"), small("0.45rem"), medium("0.625rem"), large("0.875rem")
// We extend the legacy 6-entry array shape for BlockGridTestPage compatibility,
// but the canonical RADII_LIST mirrors upstream verbatim.
// ─────────────────────────────────────────────────────────────────────────────

export const RADII_LIST: readonly RadiusPreset[] = [
  { name: 'default', label: 'Default', value: '0.625rem' }, // upstream "" resolves to default 0.625rem
  { name: 'none', label: 'None', value: '0' },
  { name: 'small', label: 'Small', value: '0.45rem' },
  { name: 'medium', label: 'Medium', value: '0.625rem' },
  { name: 'large', label: 'Large', value: '0.875rem' },
] as const

/**
 * RADII — legacy 6-entry array kept as-is for BlockGridTestPage.
 * Index 3 == "Default" 0.625rem (consumer relies on this).
 */
export const RADII: { name: string; value: string }[] = [
  { name: 'None', value: '0rem' },
  { name: 'Small', value: '0.3rem' },
  { name: 'Medium', value: '0.5rem' },
  { name: 'Default', value: '0.625rem' },
  { name: 'Large', value: '0.75rem' },
  { name: 'XL', value: '1rem' },
]

// ─────────────────────────────────────────────────────────────────────────────
// lookupPreset — single accessor that returns merged CSS vars for any combo.
// Merge order matches upstream `merge-theme.ts` + `buildRegistryTheme`:
//   light = { ...baseColor.light, ...theme?.light }
//   then chart-1..5 from chartPalette overrides per-key
//   then '--radius' override
// ─────────────────────────────────────────────────────────────────────────────

export interface LookupPresetInput {
  style: string
  baseColor: string
  theme?: string
  chartColor?: string
  radius?: string | number
  font?: string
}

export interface LookupPresetResult {
  cssVars: { light: ThemeVars; dark: ThemeVars }
  styleClass: string
  /**
   * Phase 393 — font stacks keyed by slot. Consumed by useDesignSystem →
   * ThemeTokens.fonts → getIsolatedAppStyle → --font-sans / --font-mono /
   * --font-heading CSS variables. The slot is chosen by FONT_STACKS[name].category:
   *   - category 'sans'  → { sans: stack, heading: stack }
   *   - category 'mono'  → { mono: stack }
   *   - category 'serif' → { sans: stack, heading: stack } (serif fonts used
   *     as body sans in shadcn's typography system)
   */
  fonts: { sans?: string; mono?: string; heading?: string }
  /** @deprecated Retained for applyThemeVars() legacy overload. New code should consume `fonts`. */
  fontVars: Record<string, string>
}

function resolveRadius(radius: string | number | undefined): string {
  if (radius == null) return RADII_LIST[0].value
  if (typeof radius === 'number') {
    return RADII[radius]?.value ?? RADII_LIST[0].value
  }
  // string: try RADII_LIST name first, then raw value
  const byName = RADII_LIST.find((r) => r.name === radius)
  if (byName) return byName.value
  return radius
}

/**
 * Compute merged preset for a given (style, baseColor, theme?, chartColor?, radius, font) tuple.
 *
 * Returns:
 *  - cssVars.light / cssVars.dark — full ThemeVars maps to inject as :root vars
 *  - styleClass — `style-{name}` to apply to <html>/<body>
 *  - fontVars — `--font-sans` / `--font-heading` mapping
 *
 * Throws if `style` or `baseColor` is unknown. `theme`, `chartColor`, `font`
 * are optional and silently ignored if unknown (matches upstream defensive default).
 */
export function lookupPreset(input: LookupPresetInput): LookupPresetResult {
  const stylePreset = STYLES_BY_NAME[input.style]
  if (!stylePreset) {
    throw new Error(`lookupPreset: unknown style "${input.style}"`)
  }
  const basePreset = BASE_COLORS_BY_NAME[input.baseColor]
  if (!basePreset) {
    throw new Error(`lookupPreset: unknown baseColor "${input.baseColor}"`)
  }

  const themePreset = input.theme ? THEMES[input.theme] : undefined
  const chartPreset = input.chartColor ? CHART_PALETTES[input.chartColor] : undefined

  // Merge order — identical to upstream merge-theme.ts.
  const lightMerged: ThemeVars = {
    ...basePreset.light,
    ...(themePreset?.light ?? {}),
  }
  const darkMerged: ThemeVars = {
    ...basePreset.dark,
    ...(themePreset?.dark ?? {}),
  }

  // Chart palette: only --chart-1..5 keys override (upstream buildRegistryTheme).
  if (chartPreset) {
    for (let i = 1; i <= 5; i++) {
      const key = `chart-${i}`
      if (chartPreset.light?.[key]) lightMerged[key] = chartPreset.light[key]
      if (chartPreset.dark?.[key]) darkMerged[key] = chartPreset.dark[key]
    }
  }

  // Radius override.
  // Phase 396 fix: shadcn's "Lyra" style is the sharp/angular variant —
  // upstream Lyra CSS hardcodes border-radius: 0px on every component via
  // `.style-lyra .cn-*` rules. Cogo renderers don't use `.cn-*` class names
  // (they use Tailwind utilities that reference `--radius`), so the CSS
  // cascade from shadcn-lyra-sync.css never reaches Cogo blocks. Instead,
  // force --radius to 0rem when Lyra is selected — this makes every
  // `rounded-*` Tailwind utility collapse to 0 and produces the expected
  // rectangular appearance on buttons, inputs, cards, and block wrappers.
  const radiusValue =
    input.style === 'lyra' ? '0rem' : resolveRadius(input.radius)
  lightMerged.radius = radiusValue
  darkMerged.radius = radiusValue

  // Phase 393: emit real font stacks via FONT_STACKS. Previous implementation
  // returned `var(--font-${name})` references that nothing ever defined at
  // runtime — a dead contract inherited from the upstream Next.js pattern.
  // Category routing:
  //   - mono  → only fill --font-mono (so body text keeps its current sans)
  //   - sans  → fill --font-sans AND --font-heading (body + headings)
  //   - serif → fill --font-sans AND --font-heading (shadcn treats serif
  //             fonts as body/heading replacements, not a separate slot)
  const fontStack = input.font ? FONT_STACKS[input.font] : undefined
  const fonts: { sans?: string; mono?: string; heading?: string } = {}
  const legacyFontVars: Record<string, string> = {}
  if (fontStack) {
    if (fontStack.category === 'mono') {
      fonts.mono = fontStack.stack
      legacyFontVars['--font-mono'] = fontStack.stack
      // Phase 393 WR-01: when a mono preset is selected, explicitly reset
      // --font-sans / --font-heading to the document default so the wrapper
      // does not inherit a stale value from a previously-selected sans/serif
      // font. Without this reset, toggling from "inter" → "jetbrains-mono"
      // would leave body text rendering as Inter instead of the default.
      fonts.sans = 'system-ui, sans-serif'
      fonts.heading = 'system-ui, sans-serif'
      legacyFontVars['--font-sans'] = 'system-ui, sans-serif'
      legacyFontVars['--font-heading'] = 'system-ui, sans-serif'
    } else {
      fonts.sans = fontStack.stack
      fonts.heading = fontStack.stack
      legacyFontVars['--font-sans'] = fontStack.stack
      legacyFontVars['--font-heading'] = fontStack.stack
    }
  }

  return {
    cssVars: { light: lightMerged, dark: darkMerged },
    styleClass: stylePreset.className,
    fonts,
    fontVars: legacyFontVars,
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// DOM mutators — applyThemeVars (legacy + new overload), applyStyle.
// All writes go through the SINGLE `<style id="cogo-theme-vars">` block (D-08).
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Inject (or update) the single `<style id="cogo-theme-vars">` block.
 *
 * Two call shapes (D-02 backward compat):
 *  1. Legacy: `applyThemeVars({ baseColor, radius, dark? })` — used by BlockGridTestPage.
 *  2. New: `applyThemeVars(lookupPreset(...))` — pass a LookupPresetResult; both
 *     light + dark vars and fontVars are emitted into the same block.
 */
export function applyThemeVars(
  opts:
    | { baseColor: BaseColorPreset; radius: string; dark?: boolean }
    | LookupPresetResult,
): void {
  const id = 'cogo-theme-vars'
  let el = document.getElementById(id) as HTMLStyleElement | null
  if (!el) {
    el = document.createElement('style')
    el.id = id
    document.head.appendChild(el)
  }

  // New overload: LookupPresetResult
  if ('cssVars' in opts) {
    const lightLines = Object.entries(opts.cssVars.light)
      .map(([k, v]) => `  --${k}: ${v};`)
      .join('\n')
    const darkLines = Object.entries(opts.cssVars.dark)
      .map(([k, v]) => `  --${k}: ${v};`)
      .join('\n')
    const fontLines = Object.entries(opts.fontVars)
      .map(([k, v]) => `  ${k}: ${v};`)
      .join('\n')
    el.textContent =
      `:root {\n${lightLines}\n${fontLines}\n}\n` +
      `.dark {\n${darkLines}\n${fontLines}\n}`
    return
  }

  // Legacy overload: { baseColor, radius, dark? }
  const vars = opts.dark ? opts.baseColor.dark : opts.baseColor.light
  const lines = Object.entries(vars)
    .map(([k, v]) => `  --${k}: ${v};`)
    .concat([`  --radius: ${opts.radius};`])
    .join('\n')
  el.textContent = `:root {\n${lines}\n}`
}

/**
 * Replace any existing `style-*` class on <html> with the chosen one.
 */
export function applyStyle(style: Style | string): void {
  const html = document.documentElement
  STYLES.forEach((s) => html.classList.remove(`style-${s}`))
  html.classList.add(`style-${style}`)
}
